import React, { useState, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { subDays, format } from 'date-fns';
import { FaRegCalendarAlt } from 'react-icons/fa';
import ifav from './ifav.png';
import Shimmer_graph from '../shimmer_graph';
import { ManText, ManCard } from '@craft-design-system/core-components-react';
import useChargingOverviewQuery from '../../utils/useChargingOverviewQuery';
import { useResetContext } from '../ResetContext';
import AIToggleButton from '../AIToggleButton';

const Charging_Overview_Cycle = ({ value }) => {
  const { vin_short } = value || {};
  const { resetdata } = useResetContext();

  const yesterday = subDays(new Date(), 1);
  const [selectedDate, setSelectedDate] = useState(yesterday);
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [downloadData, setDownloadData] = useState([]);

  const { fetchChargingOverview } = useChargingOverviewQuery();

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <b>Definition:</b><br />
        The overview on charging events provides a detailed insight on a 24-hour basis.<br /><br />
        <b>Insights:</b><br />
        • Start and end time of charging cycles<br />
        • Duration of each cycle<br />
        • Start and end State of Charge (SoC)<br />
        • Charging current<br />
        • Classification into Fast or Normal charging<br /><br />
        <b>Time Series Visualization:</b><br />
        If multiple vehicles are selected, a scatterplot gives an overview of all charging events over a 24-hour period.<br /><br />
        <b>Telematic Raw Data for this KPI:</b><br />
        <b>TBM3:</b><br />
        • highvoltsysmode<br />
        • batterycurrent<br />
        • energylevel<br />
        • stateofcharge<br />
        • batterytemperature<br />
        • nr_battery_packs<br /><br />
        <b>CM4:</b><br />
        • highvoltsysmode<br />
        • batterycurrent<br />
        • batcurrcbp<br />
        • energylevel<br />
        • energylevelcbp<br />
        • stateofcharge<br />
        • batterytemperature<br />
        • nr_battery_packs
      </Popover.Body>
    </Popover>
  );



  const fetchData = async (date) => {
    if (!vin_short || vin_short.length === 0) return;

    setIsLoading(true);
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');

      const result = await fetchChargingOverview({
        vin_short,
        date: formattedDate,
        category: resetdata,
      });

      if (!result) {
        setChartData(null);
        setDownloadData([]);
        return;
      }

      const parsedChart =
        typeof result.chart === "string"
          ? JSON.parse(result.chart)
          : result.chart;

      setChartData(parsedChart);
      setDownloadData(result.downloadData || []);

    } catch (error) {
      console.error("Error fetching charging overview data", error);
      setChartData(null);
      setDownloadData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(yesterday);
  }, [JSON.stringify(vin_short), resetdata]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchData(date);
  };

  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        padding: '6px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        backgroundColor: '#fff',
        fontSize: '14px',
        maxWidth: '180px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      <FaRegCalendarAlt size={20} color="#007bff" />
      <span>{value}</span>
    </div>
  ));

  return (
    <ManCard 
      className="card_charging_overview" 
      appearance="soft" 
      outline="false"
      style={{
        height: "auto",
        minHeight: "450px",
        transition: 'all 0.3s ease-in-out',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative' // 🔥 For absolute positioning of info button
      }}
    >

      {/* 🔥 Info button - Top rightmost corner */}
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        zIndex: 10
      }}>
        <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
          <img src={ifav} alt="info" style={{ width: 20, height: 20, cursor: 'pointer' }} />
        </OverlayTrigger>
      </div>

      {/* Title */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '16px',
        marginTop: '16px' // 🔥 Add margin to avoid overlap with info button
      }}>
        <ManText className="card-title-text" variant="component-l" as="span" size="l">
          Overview charging events
        </ManText>
      </div>

      {/* 🔥 Simplified Toolbar - Only date picker */}
      <div style={{
        display: 'flex',
        justifyContent: 'center', // 🔥 Center the date picker
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          maxDate={yesterday}
          minDate={subDays(yesterday, 13)}
          dateFormat="yyyy-MM-dd"
          customInput={<CustomDateInput />}
        />
      </div>

      {/* Graph container */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '12px',
        flex: 1
      }}>
        {isLoading ? (
          <div style={{ 
            minHeight: "350px",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shimmer_graph />
          </div>
        ) : chartData?.data?.length > 0 ? (
          <>
            {/* Graph */}
            <div style={{ 
              width: '100%',
              minHeight: "350px",
              height: "auto"
            }}>
              <Plot
                data={chartData.data}
                layout={{
                  ...chartData.layout,
                  autosize: true,
                  height: 400,
                  margin: { t: 10, r: 10, l: 10, b: 50 },
                }}
                config={{ 
                  responsive: true,
                  displayModeBar: true,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
                }}
                useResizeHandler={true}
                style={{ 
                  width: '100%', 
                  height: '100%'
                }}
              />
            </div>

            {/* 🔥 AI Toggle Button - Below the graph, left aligned */}
            {downloadData?.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start', // 🔥 Left align
                paddingLeft: '10px',
                marginTop: '8px', // 🔥 Small gap from graph
                marginBottom: '8px' // 🔥 Bottom margin for spacing
              }}>
                <AIToggleButton
                  downloadData={downloadData}
                  radioButtonState={{}}
                  kpiName="Charging Overview Cycle"
                  chartType="Scatter"
                  vinShort={vin_short}
                  title="AI | Automotive Intelligence - Charging Overview Cycle Analysis"
                  customStyle={{
                  backgroundColor: '#59636D',
                  borderColor: '#59636D'
                }}
                />
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            minHeight: "350px",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ManText variant="body-l">No Data Available</ManText>
          </div>
        )}
      </div>

    </ManCard>
  );
};

Charging_Overview_Cycle.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.array.isRequired,
  }).isRequired,
};

export default Charging_Overview_Cycle;